/**
 * The preview pane (decisions 9, 11, 12, 61; part 15): the page at the cursor as the layout worker drew it, the device
 * profile picker with what the profile's pages stand for (exact for the PDF, approximate for an e-reader), and each
 * page's number (an estimate until the chapter has settled, decision 10). The pane follows the open chapter and the
 * layout (table Y): it asks the worker for nothing while it isn't shown.
 *
 * The pane is a book on every profile (decision 70; parts 15a, 15b): one page, or a spread on the facing PDF profile,
 * turned by the buttons under the pages, a click or tap on them, or the wheel (tables TP, WH, BA), and on that profile a
 * page can be enlarged (table CU).
 */
import { createEffect, createMemo, createSignal, on, onCleanup } from 'solid-js';
import type { Accessor } from 'solid-js';
import { profileById } from '@cockatrice/layout';
import type { BookSlot, DeviceProfile, Place, PreviewSettings } from '@cockatrice/layout';
import { shownChapter, shownUnit } from '../preview/controller.ts';
import type { CloseUpPage, PreviewBook, PreviewDocument, PreviewState, ShownUnit } from '../preview/controller.ts';
import { bookScale, closeUpScale } from '../preview/scale.ts';
import type { PageScale } from '../preview/scale.ts';
import { swipeTo } from '../preview/swipe.ts';
import { WheelTurns } from '../preview/wheel.ts';
import type { NarrowPane, Panes } from '../shell/layout.ts';
import { cleanTitle } from '../shell/workspace.ts';
import { focusOn } from './announce.ts';
import { DeviceMenu, PROFILE_CHOSEN, dismissals } from './device-menu.ts';
import Modal from '../ui/Modal.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import { component } from '../ui/solid.ts';
import { each, el, when, whenSome } from '../ui/dom.ts';
import type { Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

export { PROFILE_CHOSEN, PROFILE_NAMES } from './device-menu.ts';

/**
 * The settings the open chapter is laid out with (part 14's page settings): the book's theme and language, the
 * profile picked on this device, and the chapter's title (cleaned as a rename cleans it) and place among the book's
 * chapters; null with no chapter open.
 */
export function previewSettings(shell: Shell): PreviewSettings | null {
  const { workspace } = shell;
  const book = workspace.book();
  const chapters = workspace.listing()?.chapters ?? [];
  const index = chapters.findIndex((entry) => entry.id === workspace.chapterId());
  const entry = chapters[index];
  if (book === null || entry === undefined) return null;
  return {
    book: book.id,
    theme: book.theme,
    profile: workspace.settings().previewProfile,
    language: book.metadata?.language ?? 'en-US',
    // Any string is stored as a title (a split takes a heading's text whole); the preview lays out what a rename would
    // store, so a long or untidy one can't fail the worker's check (V2) (deep-fix r2).
    title: cleanTitle(entry.title),
    subtitle: cleanTitle(entry.subtitle),
    image: entry.image,
    number: index + 1,
  };
}

/**
 * M24 (part 15a): the open book as the book view reads it: its live chapters in order, each with the settings it is laid
 * out with (as {@link previewSettings} gives the open one), and the store's reader of a chapter's text; null with none.
 */
export function previewBook(shell: Shell): PreviewBook | null {
  const { workspace } = shell;
  const book = workspace.book();
  const chapters = workspace.listing()?.chapters;
  if (book === null || chapters === undefined) return null;
  const profile = workspace.settings().previewProfile;
  const language = book.metadata?.language ?? 'en-US';
  return {
    chapters: chapters.map((entry, index) => ({ id: entry.id, settings: { book: book.id, theme: book.theme, profile, language, title: cleanTitle(entry.title), subtitle: cleanTitle(entry.subtitle), image: entry.image, number: index + 1 } })),
    reading: workspace.bookReading(),
    read: (chapterId) => workspace.chapterText(chapterId),
  };
}

/** The document the preview follows: the open editor with its chapter's settings, or null. */
export function previewDocument(shell: Shell): PreviewDocument | null {
  const editor = shell.workspace.editor();
  const settings = previewSettings(shell);
  if (editor === null || settings === null) return null;
  return { editor, settings, text: () => editor.parsed.text, cursor: () => editor.cursor };
}

/**
 * Y6–Y10, M1: keeps the controller told what to show and whether it is shown (the pane, the workspace's screen, the
 * page's own visibility). Runs while the workspace is in the page.
 */
export function followPreview(shell: Shell, shown: Accessor<Panes>): void {
  const { preview, workspace } = shell;
  createEffect(() => {
    preview.setVisible(shown().preview && workspace.screen() === 'workspace' && shell.pageVisible());
  });
  createEffect(() => {
    preview.setDocument(previewDocument(shell));
  });
  createEffect(() => {
    preview.setBook(previewBook(shell));
  });
  onCleanup(() => {
    preview.setVisible(false);
  });
}

/** BA1: a book view slot's caption: its chapter and page (about, while estimated), a blank page, a page being set, or nothing. */
export function slotLabel(shell: Shell, slot: BookSlot): string {
  switch (slot.kind) {
    case 'page':
      return shell.t(slot.exact ? 'preview.chapterPage' : 'preview.chapterPageAbout', { chapter: slot.chapter, number: slot.index + 1 });
    case 'blank':
      return shell.t('preview.blank');
    case 'pending':
      return shell.t('preview.pagePending');
    case 'none':
      return '';
  }
}

/** BA2, BA6: a unit's caption: its slots' captions, left then right, none of nothing. */
export function unitLabel(shell: Shell, unit: ShownUnit): string {
  const [left = '', right] = unit.slots.map((slot) => slotLabel(shell, slot)).filter((label) => label !== '');
  return right === undefined ? left : shell.t('preview.spread', { left, right });
}

/** BA2: the shown unit's caption, or nothing outside the book view. */
export function shownLabel(shell: Shell, state: PreviewState): string {
  const unit = shownUnit(state);
  return unit === null ? '' : unitLabel(shell, unit);
}

/** BA6: what a turn announces: the shown unit's caption while the book view has a place of its own, else nothing. */
export function turnNews(shell: Shell, state: PreviewState): string {
  return state.kind === 'book' && state.away ? shownLabel(shell, state) : '';
}

/** What {@link scrollsFurther} reads of an element. */
export interface ScrollBox {
  readonly scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
}

/** I3: whether `box` has further to scroll in the direction of `delta`, to within a pixel of rounding. */
export function scrollsFurther(box: ScrollBox, delta: number): boolean {
  if (delta > 0) return box.scrollTop + box.clientHeight < box.scrollHeight - 1;
  return delta < 0 && box.scrollTop > 0;
}

/** The pointer press a turn on the book's pages begins with (TP1, TP2): which pointer, and where. */
interface Press {
  readonly pointer: number;
  readonly x: number;
  readonly y: number;
}

/** TP2: how far a press may move and still turn, in CSS pixels. */
export const TURN_SLOP = 10;

/**
 * TP1–TP3, WH1–WH4: the handlers that turn the book over its pages: a press of the primary button or a touch, let go
 * where it began, right of the pages' middle forward, left of it back; a wheel gesture once. Pointer events, not
 * `click`, since a touch's click is the browser's to make (WebKit's emulated taps make none); a press the browser takes
 * for a scroll or a swipe is cancelled and never let go. A wheel with Ctrl or Meta is left to the browser (zoom).
 */
export function turnHandlers(shell: Shell, pane: () => ScrollBox): Record<string, unknown> {
  const wheel = new WheelTurns();
  let press: Press | null = null;
  const plain = (event: PointerEvent): boolean => event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
  return {
    onPointerDown: (event: On<PointerEvent, HTMLElement>) => {
      // CU6: a press on a page's own button is the button's, never a turn.
      const own = event.target instanceof Element && event.target.closest('button') !== null;
      press = plain(event) && !own && !dismissals.has(event) ? { pointer: event.pointerId, x: event.clientX, y: event.clientY } : null;
    },
    onPointerCancel: () => {
      press = null;
    },
    onPointerUp: (event: On<PointerEvent, HTMLElement>) => {
      const begun = press;
      press = null;
      if (begun?.pointer !== event.pointerId || !plain(event)) return;
      if (Math.hypot(event.clientX - begun.x, event.clientY - begun.y) >= TURN_SLOP) return;
      const box = event.currentTarget.getBoundingClientRect();
      shell.preview.turn(event.clientX >= box.left + box.width / 2 ? 1 : -1);
    },
    onWheel: (event: On<WheelEvent, HTMLElement>) => {
      const input = { deltaX: event.deltaX, deltaY: event.deltaY, deltaMode: event.deltaMode, ctrlKey: event.ctrlKey, metaKey: event.metaKey, time: event.timeStamp };
      if (input.ctrlKey || input.metaKey) return;
      // I3: pages held at their least height overflow the pane: the wheel scrolls to its end first.
      if (scrollsFurther(pane(), input.deltaY)) return;
      // The pages fit the pane, so a wheel over them turns rather than scrolls.
      event.preventDefault();
      const step = wheel.take(input);
      if (step !== 0) shell.preview.turn(step);
    },
  };
}

/** S1–S5: the handlers that switch a narrow screen's pane with a swipe over `from`. */
export function swipeHandlers(shell: Shell, from: NarrowPane): Record<string, unknown> {
  let start: { readonly pointer: string; readonly x: number; readonly y: number; readonly time: number } | null = null;
  return {
    onPointerDown: (event: On<PointerEvent, HTMLElement>) => {
      start = { pointer: event.pointerType, x: event.clientX, y: event.clientY, time: event.timeStamp };
    },
    onPointerCancel: () => {
      start = null;
    },
    onPointerUp: (event: On<PointerEvent, HTMLElement>) => {
      const begun = start;
      start = null;
      if (begun === null || shell.mode() !== 'narrow') return;
      const box = event.currentTarget.getBoundingClientRect();
      const to = swipeTo(from, {
        pointer: begun.pointer,
        startX: begun.x - box.left,
        startY: begun.y - box.top,
        endX: event.clientX - box.left,
        endY: event.clientY - box.top,
        width: box.width,
        startTime: begun.time,
        endTime: event.timeStamp,
      });
      if (to !== null) shell.ui.setNarrowPane(to);
    },
  };
}

/**
 * One side of a look in the book view (BA1): a page's canvas, given the worker's pixels once, with its caption and text;
 * a blank page or a page being set as plain paper with its caption; nothing as the space a page would take. Hidden while
 * it isn't in the shown unit, so a turn within the window shows it at once (principle 5).
 */
function BookSlotView(shell: Shell, slot: BookSlot, size: Accessor<PageScale | null>, shown: Accessor<boolean>, enlarge: ((target: CloseUpTarget) => void) | null): Child {
  const box = (): Record<string, string> => {
    const scale = size();
    return scale === null ? {} : { width: `${scale.width}px`, height: `${scale.height}px` };
  };
  const caption = slotLabel(shell, slot);
  switch (slot.kind) {
    case 'page':
      return el(
        'figure',
        { class: 'preview-page', hidden: () => !shown(), 'data-slot': 'page' },
        el('canvas', {
          'aria-hidden': 'true',
          ref: (canvas: HTMLCanvasElement) => {
            if (slot.bitmap !== null) shell.present(canvas, slot.bitmap);
          },
          style: box,
        }),
        el('figcaption', {}, caption),
        enlarge === null
          ? null
          : el(
              'button',
              {
                type: 'button',
                class: 'preview-enlarge',
                'data-enlarge': `${String(slot.chapter)}:${String(slot.index)}`,
                // CU16: the page drawn again takes this button with it: its new one keeps a keyboard writer's place.
                ref: (element: HTMLElement) => {
                  onCleanup(() => {
                    if (document.activeElement !== element) return;
                    queueMicrotask(() => {
                      focusOn(shell, `[data-enlarge="${String(slot.chapter)}:${String(slot.index)}"]`);
                    });
                  });
                },
                'aria-label': shell.t('preview.enlargePage', { page: caption }),
                onClick: (event: On<MouseEvent, HTMLElement>) => {
                  enlarge({ place: { chapter: slot.chapter, page: slot.index }, caption, opener: event.currentTarget });
                },
              },
              shell.t('preview.enlarge'),
            ),
        slot.text === null ? null : el('div', { class: 'visually-hidden page-text' }, slot.text),
      );
    case 'blank':
    case 'pending':
      return el('figure', { class: `preview-page ${slot.kind}`, hidden: () => !shown(), 'data-slot': slot.kind }, el('div', { class: 'preview-paper', style: box }), el('figcaption', {}, caption));
    case 'none':
      return el('div', { class: 'preview-page none', hidden: () => !shown(), 'data-slot': 'none', 'aria-hidden': 'true' }, el('div', { class: 'preview-space', style: box }));
  }
}

/** I6: CSS pixels of room between the bar and the foot under which they stop holding their places and scroll with the page. */
export const TIGHT_ROOM = 160;

/** Part 15b, table CU: the page a close-up shows, and its caption. */
interface CloseUpTarget {
  readonly place: Place;
  readonly caption: string;
  /** CU9: the button that opened it, which takes the focus back (WebKit gives a pressed button none to restore). */
  readonly opener: HTMLElement;
}

/**
 * CU1–CU5: the close-up of a page, in a window over the book: drawn apart at the room the window gives it (never the book's
 * own drawing, so it is sharp), with its text for assistive technology. Closing leaves the book where it was.
 */
function CloseUp(shell: Shell, profile: DeviceProfile, target: CloseUpTarget, onClose: () => void): Child {
  const { t, preview } = shell;
  const [size, setSize] = createSignal<PageScale | null>(null);
  const [page, setPage] = createSignal<CloseUpPage | null>(null);
  const [failed, setFailed] = createSignal(false);
  // CU15: no shortcut runs under the window, for as long as it is here, however it goes.
  shell.ui.setCloseUpOpen(true);
  onCleanup(() => {
    shell.ui.setCloseUpOpen(false);
  });
  return component(Modal, {
    open: true,
    size: 'lg',
    title: t('preview.closeUp', { page: target.caption }),
    onClose,
    children: el(
      'div',
      {
        class: 'preview-close-up',
        // CU10: the page can be taller than the window, and the keys scroll what holds the focus.
        tabindex: 0,
        role: 'group',
        'aria-label': t('preview.closeUp', { page: target.caption }),
        ref: (element: HTMLElement) => {
          onCleanup(
            shell.watchSize(element, (width) => {
              if (size() !== null) return;
              const scale = closeUpScale(profile, width, shell.pixelRatio());
              if (scale === null) return;
              setSize(scale);
              void preview.closeUp(target.place, scale.scale).then((drawn) => {
                setPage(drawn);
                setFailed(drawn === null);
              });
            }),
          );
        },
      },
      () => {
        const drawn = page();
        const scale = size();
        if (failed()) return el('p', { class: 'preview-status', role: 'alert' }, t('preview.closeUpFailed'));
        if (drawn === null || scale === null) return el('p', { class: 'preview-status', role: 'status' }, t('preview.loading'));
        return el(
          'figure',
          { class: 'preview-page' },
          el('canvas', {
            'aria-hidden': 'true',
            ref: (canvas: HTMLCanvasElement) => {
              shell.present(canvas, drawn.bitmap);
            },
            // CU14: at most the window's width, its shape kept, so a phone turned upright leaves Close on the screen.
            style: { width: `${String(scale.width)}px`, 'max-width': '100%', 'aspect-ratio': `${String(scale.width)} / ${String(scale.height)}` },
          }),
          drawn.text === null ? null : el('div', { class: 'visually-hidden page-text' }, drawn.text),
        );
      },
    ),
  });
}

/** Part 15b, table CH: Previous Chapter and Next Chapter, under the turns; BA5 as they: one that can't go stays focusable and says so. */
function ChapterButtons(shell: Shell): Child {
  const { t, preview, workspace } = shell;
  const button = (step: 1 | -1): Child =>
    el(
      'button',
      {
        type: 'button',
        class: step === 1 ? 'preview-next-chapter' : 'preview-previous-chapter',
        'aria-disabled': () => {
          const from = shownChapter(preview.state());
          const count = workspace.listing()?.chapters.length ?? 0;
          return from === null || from + step < 1 || from + step > count ? 'true' : undefined;
        },
        onClick: () => {
          preview.chapter(step);
        },
      },
      t(step === 1 ? 'preview.nextChapter' : 'preview.previousChapter'),
    );
  return el('div', { class: 'preview-chapters', role: 'group', 'aria-label': t('preview.chapters') }, button(-1), button(1));
}

/** BA3–BA5, BA7, BA13: the book view's turns under the pages: Back, Next, and Back to the cursor while it has a place of its own. */
function TurnButtons(shell: Shell, single: Accessor<boolean>): Child {
  const { t, preview } = shell;
  const book = createMemo(() => {
    const now = preview.state();
    return now.kind === 'book' ? now : null;
  });
  // BA5: a button that can't turn stays focusable, says so, and does nothing.
  const button = (step: 1 | -1): Child =>
    el(
      'button',
      {
        type: 'button',
        class: step === 1 ? 'preview-next' : 'preview-previous',
        'aria-label': () => (step === 1 ? t(single() ? 'preview.nextPage' : 'preview.nextPages') : t(single() ? 'preview.previousPage' : 'preview.previousPages')),
        'aria-disabled': () => {
          const now = book();
          return now === null || (step === 1 ? now.end : now.start) ? 'true' : undefined;
        },
        onClick: () => {
          preview.turn(step);
        },
      },
      step === 1 ? t('preview.next') : t('preview.previous'),
    );
  return el(
    'div',
    { class: 'preview-turns', role: 'group', 'aria-label': t('preview.turns') },
    button(-1),
    // Between the two, so Next never moves from under the pointer when this comes or goes.
    when(
      () => book()?.away === true,
      () =>
        el(
          'button',
          {
            type: 'button',
            class: 'preview-back',
            onClick: () => {
              preview.backToCursor();
              // BA7: the button goes once the pages are back at the cursor; Next takes the focus first.
              focusOn(shell, '.preview-next');
            },
          },
          t('preview.backToCursor'),
        ),
    ),
    button(1),
  );
}

/**
 * The pane: the profile picker and its note, and the pages or what stands in for them; inert while `covered` (Y3). While
 * it is `alone` (a narrow screen showing it in the text's place, Y9) it is the page's main content (part 11c: a page has
 * one main landmark, and the text's is hidden then).
 */
export function PreviewPane(shell: Shell, covered: Accessor<boolean>, alone: Accessor<boolean>): Child {
  const { t, workspace, preview } = shell;
  const profile = createMemo(() => profileById(workspace.settings().previewProfile));
  const [width, setWidth] = createSignal(0);
  const [height, setHeight] = createSignal(0);
  const [tight, setTight] = createSignal(false);
  const [closeUp, setCloseUp] = createSignal<CloseUpTarget | null>(null);
  // Z7: what the bar and the foot take of the pane, read through their refs; the bar's is watched before the foot is made.
  let barHeight: () => number;
  let footHeight = (): number => 0;
  let paneHeight = 0;
  let paneBottom: () => number;
  let paneBox = document.documentElement;
  // Z7: the pages' room is the pane's height between the bar and the foot.
  const measure = (): void => {
    const room = paneHeight - barHeight() - footHeight();
    setHeight(room);
    setTight(room < TIGHT_ROOM);
  };
  // Z6–Z10: the pages fit the room; two only on the facing profile, where they fit and the writer hasn't chosen one.
  const size = createMemo(() => bookScale(profile(), width(), height(), shell.pixelRatio(), workspace.settings().bookOnePage));
  // The mode first: the scale is what lets the first request go, and it must go in the pane's mode.
  createEffect(() => {
    const mode = size()?.mode;
    if (mode !== undefined) preview.setMode(mode);
  });
  createEffect(() => {
    preview.setScale(size()?.scale ?? null);
  });
  const state = preview.state;
  // The book view's slots, every unit's in one list (kept by slot, so a held page keeps its canvas), and the shown unit.
  const slots = createMemo(() => {
    const now = state();
    return now.kind === 'book' ? now.units.flatMap((unit) => unit.slots) : [];
  });
  const shown = createMemo(() => shownUnit(state()));
  // I5: another page opens at its top, whatever brought it, where pages held at their least height overflow the pane.
  const shownPlace = createMemo(() => JSON.stringify(shown()?.place ?? null));
  createEffect(
    on(
      shownPlace,
      () => {
        paneBox.scrollTop = 0;
      },
      { defer: true },
    ),
  );
  return el(
    // A section, which may take either role (an `aside` may not be the main content).
    'section',
    {
      class: 'preview-pane',
      'data-tight': () => String(tight()),
      'aria-label': t('preview.label'),
      role: () => (alone() ? 'main' : 'complementary'),
      inert: covered,
      ref: (element: HTMLElement) => {
        paneBottom = () => element.getBoundingClientRect().bottom;
        paneBox = element;
        onCleanup(
          shell.watchSize(element, (paneWidth, nowHeight) => {
            setWidth(paneWidth);
            paneHeight = nowHeight;
            // I1: a short screen's rule ends the pane with the window from here.
            element.style.setProperty('--pane-top', `${String(element.offsetTop)}px`);
            measure();
          }),
        );
      },
      ...swipeHandlers(shell, 'preview'),
    },
    el(
      'div',
      {
        class: 'preview-bar',
        ref: (element: HTMLElement) => {
          barHeight = () => element.offsetHeight;
          // Z7: another page size, or the One page / Two pages switch, can change the bar's height.
          onCleanup(shell.watchSize(element, measure));
        },
      },
      // Part 15c: the page size as a menu of devices (decision 11).
      DeviceMenu(shell, () => paneBottom()),
      // Decision 70: the writer's choice, only where it is one: the facing profile, with room for two.
      when(
        () => size()?.twoFit === true,
        () =>
          component(SegmentedControl, {
            label: t('preview.pagesShown'),
            class: 'preview-page-count',
            size: 'sm',
            options: [
              { value: 'one', label: t('preview.onePage') },
              { value: 'two', label: t('preview.twoPages') },
            ],
            get value() {
              return workspace.settings().bookOnePage ? 'one' : 'two';
            },
            onChange: (value: string) => {
              void workspace.updateSettings({ bookOnePage: value === 'one' });
            },
          }),
      ),
      el('p', { class: 'preview-note' }, () => (profile().approximate ? t('preview.approximate') : t('preview.exact'))),
    ),
    () => {
      switch (state().kind) {
        case 'none':
          return el('p', { class: 'preview-status' }, t('preview.none'));
        case 'loading':
          return el('p', { class: 'preview-status', role: 'status' }, t('preview.loading'));
        case 'failed':
          return el(
            'div',
            { class: 'preview-status', role: 'alert' },
            el('p', {}, t('preview.failed')),
            el(
              'button',
              {
                type: 'button',
                onClick: () => {
                  preview.restart();
                  // Part 11c, A24: the button goes with the failure; the pane's first control takes the focus.
                  focusOn(shell, PROFILE_CHOSEN);
                },
              },
              t('preview.retry'),
            ),
          );
        case 'book':
          return null;
      }
    },
    when(
      () => state().kind === 'book',
      () =>
        el(
          'div',
          {
            class: 'preview-pages book',
            role: 'group',
            'aria-label': () => shownLabel(shell, state()),
            ...turnHandlers(shell, () => paneBox),
          },
          each(slots, (slot) => BookSlotView(shell, slot, size, () => shown()?.slots.includes(slot) === true, profile().facing ? setCloseUp : null)),
        ),
    ),
    el(
      'div',
      {
        class: 'preview-foot',
        ref: (element: HTMLElement) => {
          footHeight = () => element.offsetHeight;
          // Z7: Back to the cursor can wrap the foot onto another line.
          onCleanup(shell.watchSize(element, measure));
        },
      },
      TurnButtons(shell, () => size()?.mode === 'page'),
      ChapterButtons(shell),
    ),
    whenSome(closeUp, (target) =>
      CloseUp(shell, profile(), target(), () => {
        const { opener, place } = target();
        setCloseUp(null);
        // CU9: the page may have been drawn again meanwhile, and the button with it: its new button takes the focus.
        if (opener.isConnected) opener.focus();
        else focusOn(shell, `[data-enlarge="${String(place.chapter)}:${String(place.page)}"]`);
      }),
    ),
  );
}
